<template>
  <div class="pricing-page">

    <!-- Header -->
    <div class="pricing-hero">
      <div class="container">
        <div class="hero-back">
          <router-link :to="{ name: 'login' }" class="back-link">← Zurück zum Login</router-link>
        </div>
        <h1 class="hero-title">Preisliste</h1>
        <p class="hero-subtitle">As-a-Service · Energiemesstechnik für Unternehmen</p>
        <p class="hero-note">Monatlich kündbar &nbsp;·&nbsp; Jahresabos mit <strong>10 % Rabatt</strong> auf die Basispauschale</p>

        <!-- Billing toggle -->
        <div class="billing-toggle">
          <span :class="{ active: billingCycle === 'monthly' }" @click="billingCycle = 'monthly'">Monatlich</span>
          <div class="toggle-switch" @click="billingCycle = billingCycle === 'monthly' ? 'annual' : 'monthly'">
            <div class="toggle-knob" :class="{ right: billingCycle === 'annual' }"></div>
          </div>
          <span :class="{ active: billingCycle === 'annual' }" @click="billingCycle = 'annual'">
            Jährlich <span class="badge-save">–10 %</span>
          </span>
        </div>
      </div>
    </div>

    <div class="container pricing-body">

      <!-- 1. Plan cards -->
      <section class="section-plans">
        <div class="row g-4 justify-content-center">

          <!-- Basic -->
          <div class="col-12 col-md-4">
            <div class="plan-card">
              <div class="plan-header">
                <div class="plan-name">Basic</div>
                <div class="plan-price">
                  <span class="price-amount">{{ billingCycle === 'annual' ? '26' : '29' }}</span>
                  <span class="price-unit">€ / Monat</span>
                </div>
                <div v-if="billingCycle === 'annual'" class="price-billed">Abrechnung 312 € / Jahr</div>
                <div v-else class="price-billed">&nbsp;</div>
              </div>
              <ul class="plan-features">
                <li><span class="feat-yes">✓</span> Momentandaten &amp; Zählerstände</li>
                <li><span class="feat-no">–</span> Lastgangauswertung</li>
                <li><span class="feat-addon">+</span> CO₂-Bericht <small>(9,90 € / Bericht)</small></li>
                <li><span class="feat-no">–</span> Anomalie-Alerts</li>
                <li><span class="feat-yes">✓</span> Datenspeicherung unbegrenzt</li>
                <li><span class="feat-yes">✓</span> 3 User inkl. / +5 € pro User</li>
                <li><span class="feat-addon">+</span> API-Zugang <small>(29 € / Monat)</small></li>
                <li><span class="feat-no">–</span> Mehrstandort-Dashboard</li>
                <li><span class="feat-yes">✓</span> E-Mail-Support</li>
              </ul>
              <div class="plan-cta">
                <router-link :to="{ name: 'login' }" class="btn btn-outline-plan">Jetzt starten</router-link>
              </div>
            </div>
          </div>

          <!-- Pro -->
          <div class="col-12 col-md-4">
            <div class="plan-card plan-card-highlight">
              <div class="plan-badge">Empfohlen</div>
              <div class="plan-header">
                <div class="plan-name">Pro <span class="star">★</span></div>
                <div class="plan-price">
                  <span class="price-amount">{{ billingCycle === 'annual' ? '53' : '59' }}</span>
                  <span class="price-unit">€ / Monat</span>
                </div>
                <div v-if="billingCycle === 'annual'" class="price-billed">Abrechnung 636 € / Jahr</div>
                <div v-else class="price-billed">&nbsp;</div>
              </div>
              <ul class="plan-features">
                <li><span class="feat-yes">✓</span> Momentandaten &amp; Zählerstände</li>
                <li><span class="feat-yes">✓</span> Lastgangauswertung</li>
                <li><span class="feat-yes">✓</span> CO₂-Bericht wöchentlich + PDF</li>
                <li><span class="feat-yes">✓</span> Anomalie-Alerts</li>
                <li><span class="feat-yes">✓</span> Datenspeicherung unbegrenzt</li>
                <li><span class="feat-yes">✓</span> 5 User inkl. / +5 € pro User</li>
                <li><span class="feat-addon">+</span> API-Zugang <small>(29 € / Monat)</small></li>
                <li><span class="feat-no">–</span> Mehrstandort-Dashboard</li>
                <li><span class="feat-yes">✓</span> E-Mail-Support</li>
              </ul>
              <div class="plan-cta">
                <router-link :to="{ name: 'login' }" class="btn btn-plan">Jetzt starten</router-link>
              </div>
            </div>
          </div>

          <!-- Enterprise -->
          <div class="col-12 col-md-4">
            <div class="plan-card">
              <div class="plan-header">
                <div class="plan-name">Enterprise</div>
                <div class="plan-price">
                  <span class="price-amount">{{ billingCycle === 'annual' ? '107' : '119' }}</span>
                  <span class="price-unit">€ / Monat</span>
                </div>
                <div v-if="billingCycle === 'annual'" class="price-billed">Abrechnung 1.284 € / Jahr</div>
                <div v-else class="price-billed">&nbsp;</div>
              </div>
              <ul class="plan-features">
                <li><span class="feat-yes">✓</span> Momentandaten &amp; Zählerstände</li>
                <li><span class="feat-yes">✓</span> Lastgangauswertung</li>
                <li><span class="feat-yes">✓</span> CO₂-Bericht wöchentlich + PDF</li>
                <li><span class="feat-yes">✓</span> Anomalie-Alerts</li>
                <li><span class="feat-yes">✓</span> Datenspeicherung unbegrenzt</li>
                <li><span class="feat-yes">✓</span> Unbegrenzte User</li>
                <li><span class="feat-yes">✓</span> API-Zugang inklusive</li>
                <li><span class="feat-yes">✓</span> Mehrstandort-Dashboard</li>
                <li><span class="feat-yes">✓</span> Dedizierter Support + SLA 99,5 %</li>
              </ul>
              <div class="plan-cta">
                <router-link :to="{ name: 'login' }" class="btn btn-outline-plan">Jetzt starten</router-link>
              </div>
            </div>
          </div>

        </div>
        <p class="annual-note" v-if="billingCycle === 'annual'">
          * 10 % Rabatt gilt ausschließlich auf die Basispauschale. Hardware-Miete und Datenhaltung bleiben unverändert. Abrechnung jährlich im Voraus.
        </p>
      </section>

      <!-- 2. Datenhaltung per sensor -->
      <section class="pricing-section">
        <h2 class="section-title">Datenhaltung pro Sensor</h2>
        <p class="section-desc">Gilt für jeden angebundenen Sensor, unabhängig vom Zählertyp (Strom, Wasser, Gas, Wärme).</p>
        <div class="table-responsive">
          <table class="pricing-table">
            <thead>
              <tr>
                <th>Sensortyp</th>
                <th>Beschreibung</th>
                <th class="text-center">Basic</th>
                <th class="text-center">Pro</th>
                <th class="text-center">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>HW-Sensor (gemietet)</td>
                <td>Im Mietpreis inkludiert</td>
                <td class="text-center price-zero">0,00 €</td>
                <td class="text-center price-zero">0,00 €</td>
                <td class="text-center price-zero">0,00 €</td>
              </tr>
              <tr>
                <td>HW-Sensor (gekauft)</td>
                <td>Datenhaltung separat abgerechnet</td>
                <td class="text-center">3,50 € / Monat</td>
                <td class="text-center">2,90 € / Monat</td>
                <td class="text-center">2,20 € / Monat</td>
              </tr>
              <tr>
                <td>Manueller Sensor</td>
                <td>Virtuelle Datenpunkte (Müll, Diesel, Gas-Leckagen etc.)</td>
                <td class="text-center">1,00 € / Monat</td>
                <td class="text-center">1,00 € / Monat</td>
                <td class="text-center">1,00 € / Monat</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 3. Hardware rental -->
      <section class="pricing-section">
        <h2 class="section-title">Hardware-Miete</h2>
        <div class="table-responsive">
          <table class="pricing-table">
            <thead>
              <tr>
                <th>Gerät</th>
                <th class="text-right">Miete / Monat</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Controller / Gateway (CDC)</td>
                <td class="text-right">7,90 €</td>
              </tr>
              <tr>
                <td>Funk-Sensor <small class="text-muted">(Datenhaltung inkl.)</small></td>
                <td class="text-right">4,90 €</td>
              </tr>
              <tr>
                <td>LTE-Modem <small class="text-muted">(Modem + Carrier-Gebühren)</small></td>
                <td class="text-right">9,90 €</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 4. One-time fees -->
      <section class="pricing-section">
        <h2 class="section-title">Einmalige Gebühren</h2>
        <div class="table-responsive">
          <table class="pricing-table">
            <thead>
              <tr>
                <th>Position</th>
                <th class="text-right">Preis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Einrichtungsgebühr <small class="text-muted">(Setup, Inbetriebnahme, Portal-Konfiguration)</small></td>
                <td class="text-right">149,00 €</td>
              </tr>
              <tr>
                <td>SIM-Datenkarte <small class="text-muted">(einmalig, physisch)</small></td>
                <td class="text-right">9,90 €</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 5. Add-ons -->
      <section class="pricing-section">
        <h2 class="section-title">Add-ons</h2>
        <div class="table-responsive">
          <table class="pricing-table">
            <thead>
              <tr>
                <th>Add-on</th>
                <th>Beschreibung</th>
                <th class="text-right">Preis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CO₂-Bericht <small class="text-muted">(Basic)</small></td>
                <td>Einzelabruf, PDF-Export</td>
                <td class="text-right">9,90 € / Bericht</td>
              </tr>
              <tr>
                <td>Zusätzlicher User-Account <small class="text-muted">(Basic/Pro)</small></td>
                <td>Pro weiterer Nutzer</td>
                <td class="text-right">5,00 € / Monat</td>
              </tr>
              <tr>
                <td>API-Zugang <small class="text-muted">(Basic/Pro)</small></td>
                <td>REST-API für ERP, BMS u. a.</td>
                <td class="text-right">29,00 € / Monat</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 6. Example calculation -->
      <section class="pricing-section">
        <h2 class="section-title">Beispielrechnung</h2>
        <p class="section-desc">Typisches Setup: Paket Pro · 1 Controller · 7 HW-Sensoren + 3 Manuelle Sensoren · LTE-Add-on · 5 User (2 zusätzlich)</p>
        <div class="table-responsive">
          <table class="pricing-table example-table">
            <thead>
              <tr>
                <th>Position</th>
                <th class="text-right">Monatlich</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Pro-Basispauschale</td>
                <td class="text-right">59,00 €</td>
              </tr>
              <tr>
                <td>Controller-Miete × 1</td>
                <td class="text-right">7,90 €</td>
              </tr>
              <tr>
                <td>HW-Sensor-Miete × 7 × 4,90 € <small class="text-muted">(inkl. Datenhaltung)</small></td>
                <td class="text-right">34,30 €</td>
              </tr>
              <tr>
                <td>Datenhaltung HW-Sensoren <small class="text-muted">(gemietet, 0,00 €)</small></td>
                <td class="text-right price-zero">0,00 €</td>
              </tr>
              <tr>
                <td>Datenhaltung Manuelle Sensoren × 3 × 1,00 €</td>
                <td class="text-right">3,00 €</td>
              </tr>
              <tr>
                <td>LTE-Modem</td>
                <td class="text-right">9,90 €</td>
              </tr>
              <tr>
                <td>2 zusätzliche User × 5,00 €</td>
                <td class="text-right">10,00 €</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td>Gesamt / Monat <small>(monatlich kündbar)</small></td>
                <td class="text-right">124,10 €</td>
              </tr>
              <tr class="total-row total-row-annual">
                <td>Gesamt / Monat <small>(Jahresabo, –10 % auf Basispauschale)</small></td>
                <td class="text-right">118,10 €</td>
              </tr>
              <tr>
                <td>Einrichtungsgebühr <small>(einmalig)</small></td>
                <td class="text-right">149,00 €</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <!-- Footer note -->
      <section class="pricing-footnote">
        <p>
          * Datenspeicherung unbegrenzt solange Abo aktiv. Datenhaltung für gemietete HW-Sensoren im Mietpreis inkludiert (0,00 €).
          Manuelle Sensoren: virtuelle Datenpunkte für manuelle Werteingaben (Müll, Diesel, Gas-Leckagen etc.) à 1,00 €/Monat.
          Basic &amp; Pro: 3 User inkl., weitere à 5 €/Monat. Enterprise: unbegrenzte User.
          Jahresabo: 10 % Rabatt nur auf Basispauschale, Abrechnung jährlich im Voraus.
        </p>
        <div class="footer-cta">
          <router-link :to="{ name: 'login' }" class="btn btn-plan btn-lg">Jetzt einloggen</router-link>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PricingPage',
  data () {
    return {
      billingCycle: 'monthly'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';

// ─── Hero ────────────────────────────────────────────────────────────────────
.pricing-hero {
  background: $app-primary;
  color: #fff;
  padding: 48px 0 40px;
  text-align: center;

  .hero-back {
    text-align: left;
    margin-bottom: 16px;
  }

  .back-link {
    color: rgba(255,255,255,0.75);
    font-size: 13px;
    text-decoration: none;

    &:hover { color: #fff; }
  }

  .hero-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .hero-subtitle {
    font-size: 1rem;
    opacity: 0.85;
    margin-bottom: 4px;
  }

  .hero-note {
    font-size: 0.875rem;
    opacity: 0.7;
    margin-bottom: 24px;
  }
}

// ─── Billing toggle ───────────────────────────────────────────────────────────
.billing-toggle {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.15);
  border-radius: 30px;
  padding: 6px 16px;
  font-size: 0.9rem;
  cursor: pointer;

  span {
    opacity: 0.65;
    transition: opacity 0.2s;
    &.active { opacity: 1; font-weight: 600; }
  }

  .toggle-switch {
    width: 40px;
    height: 22px;
    background: rgba(255,255,255,0.3);
    border-radius: 11px;
    position: relative;
    cursor: pointer;

    .toggle-knob {
      position: absolute;
      top: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      background: #fff;
      border-radius: 50%;
      transition: left 0.2s;

      &.right { left: 21px; }
    }
  }

  .badge-save {
    background: #e8f5e9;
    color: #2e7d32;
    font-size: 0.7rem;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 600;
  }
}

// ─── Body ─────────────────────────────────────────────────────────────────────
.pricing-body {
  padding-top: 48px;
  padding-bottom: 64px;
}

// ─── Plan cards ───────────────────────────────────────────────────────────────
.section-plans {
  margin-bottom: 56px;
}

.plan-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: visible;
  position: relative;
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  transition: box-shadow 0.2s;

  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.1); }

  &.plan-card-highlight {
    border-color: $app-primary;
    border-width: 2px;
    box-shadow: 0 4px 16px rgba(76,74,70,0.18);
  }
}

.plan-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: $app-primary;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 14px;
  border-radius: 20px;
  white-space: nowrap;
}

.plan-header {
  padding: 28px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}

.plan-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;

  .star { color: $app-primary; }
}

.plan-price {
  line-height: 1;
  margin-bottom: 4px;

  .price-amount {
    font-size: 2.4rem;
    font-weight: 700;
    color: $app-primary;
  }

  .price-unit {
    font-size: 0.85rem;
    color: #888;
    margin-left: 4px;
  }
}

.price-billed {
  font-size: 0.78rem;
  color: #aaa;
  min-height: 18px;
}

.plan-features {
  list-style: none;
  padding: 16px 24px;
  margin: 0;
  flex: 1;

  li {
    padding: 6px 0;
    font-size: 0.875rem;
    color: #444;
    border-bottom: 1px solid #f5f5f5;
    display: flex;
    align-items: flex-start;
    gap: 8px;

    &:last-child { border-bottom: none; }

    small { color: #888; }
  }
}

.feat-yes  { color: #2e7d32; font-weight: 700; flex-shrink: 0; }
.feat-no   { color: #bbb; font-weight: 700; flex-shrink: 0; }
.feat-addon { color: $app-outline-info; font-weight: 700; flex-shrink: 0; }

.plan-cta {
  padding: 16px 24px 24px;
  text-align: center;
}

.btn-plan {
  background: $app-primary;
  border: 2px solid $app-primary;
  color: #fff;
  border-radius: 8px;
  padding: 8px 28px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: $app-primary-hover;
    border-color: $app-primary-hover;
    color: #fff;
  }
}

.btn-outline-plan {
  background: transparent;
  border: 2px solid $app-primary;
  color: $app-primary;
  border-radius: 8px;
  padding: 8px 28px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: $app-primary;
    color: #fff;
  }
}

.annual-note {
  font-size: 0.78rem;
  color: #888;
  text-align: center;
  margin-top: 16px;
}

// ─── Sections ─────────────────────────────────────────────────────────────────
.pricing-section {
  margin-bottom: 48px;

  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 6px;
    padding-bottom: 10px;
    border-bottom: 2px solid $app-primary;
    display: inline-block;
  }

  .section-desc {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 16px;
  }
}

// ─── Tables ───────────────────────────────────────────────────────────────────
.pricing-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  thead tr {
    background: $app-primary;
    color: #fff;
  }

  th {
    padding: 10px 14px;
    font-weight: 600;
    text-align: left;
  }

  td {
    padding: 9px 14px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
  }

  tbody tr:hover td { background: #fafafa; }

  &.example-table tfoot {
    .total-row td {
      font-weight: 700;
      color: $app-primary;
      border-top: 2px solid $app-primary;
      font-size: 0.95rem;
    }

    .total-row-annual td {
      font-weight: 600;
      color: #2e7d32;
      border-top: none;
      font-size: 0.875rem;
    }
  }
}

.text-right { text-align: right; }
.price-zero { color: #aaa; }

// ─── Footnote ─────────────────────────────────────────────────────────────────
.pricing-footnote {
  border-top: 1px solid #e0e0e0;
  padding-top: 24px;
  text-align: center;

  p {
    font-size: 0.78rem;
    color: #888;
    max-width: 720px;
    margin: 0 auto 24px;
    line-height: 1.6;
  }
}
</style>
